import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { Modal, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FormActions from '../../../components/FormActions';
import { facialService } from '../../../services/central-api/facial';
import { WindowInformation } from '../../../services/window-information';
import { useForm } from 'react-hook-form';
import MyCpfInput from '../../../components/MyCpfInput';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import { ClientError } from '../../../services/central-api/base-service';

interface ModalParams {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    onClose: () => void;
    ignoredPerson?: IgnoredPerson;
}

export default function IgnoredPersonModal({ isModalVisible, setModalVisible, onClose, ignoredPerson }: ModalParams) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultEmptyValues: Omit<IgnoredPerson, 'id' | 'createdAt'> = {
        document: '',
    };

    const form = useForm<Omit<IgnoredPerson, 'id'>>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    useEffect(() => {
        if (!ignoredPerson) {
            form.reset(defaultEmptyValues);
            return;
        }
        form.reset(ignoredPerson);
    }, [ignoredPerson]);

    async function formSubmit() {
        try {
            if (ignoredPerson?.id) {
                await facialService.updateIgnoredPerson(ignoredPerson.id, form.getValues());
            } else {
                await facialService.createIgnoredPerson(form.getValues());
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            form.reset(defaultEmptyValues);
            onClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal visible={isModalVisible} animationType='fade' onRequestClose={() => {
            setModalVisible(false);
            onClose();
            form.reset(defaultEmptyValues);
        }} transparent={true}>
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('registerDocumentToBeIgnored')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('registerDocumentToBeIgnoredDescription')}</MyAppText>
                    </View>

                    <MyCpfInput
                        viewStyle={{ flex: 1 }}
                        form={form}
                        fieldName='document'
                        required
                    />

                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            onClose();
                            form.reset(defaultEmptyValues);
                        }}
                        disabled={!form.formState.isValid}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.card.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
}));
